import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { 
  Check, 
  X, 
  MessageCircle, 
  ExternalLink, 
  Loader2, 
  RefreshCcw, 
  User, 
  Mail, 
  Calendar, 
  Trophy,
  Link as LinkIcon,
  MessageSquare,
  Eye,
  Image as ImageIcon,
} from 'lucide-react';
import api from '@/services/api';
import { getSignedUrlForFile } from '@/services/uploadService';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from '@/components/ui/textarea';

type CreatorApplicationStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REQUEST_INFO';

interface ArtworkAsset {
  key: string;
  label: string;
  href: string | null;
  thumbnail: string | null;
}

interface CreatorApplication {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  requestedLevel: string;
  status: CreatorApplicationStatus;
  artworkLinks: string[];
  socialLinks: string[];
  socialMediaLinks: string[];
  websiteLinks: string[];
  shopLinks: string[];
  artworkFiles: ArtworkAsset[];
  communicationMethod: string;
  comment: string;
  createdAt: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map(asString)
    .filter((entry) => entry.length > 0);
};

const mergeUniqueStrings = (...arrays: string[][]): string[] =>
  [...new Set(arrays.flat().map((item) => item.trim()).filter(Boolean))];

const normalizeStatus = (value: unknown): CreatorApplicationStatus => {
  const status = asString(value);
  return status === 'APPROVED' || status === 'REJECTED' || status === 'REQUEST_INFO'
    ? status
    : 'PENDING';
};

const normalizeListApplication = (raw: unknown): CreatorApplication => {
  const data = isRecord(raw) ? raw : {};
  const user = isRecord(data.user) ? data.user : {};

  return {
    id: asString(data.id),
    userId: asString(data.userId),
    userEmail: asString(data.userEmail) || asString(user.email),
    userName: asString(data.userName) || asString(user.name),
    requestedLevel: asString(data.requestedLevel) || 'CREATOR_20',
    status: normalizeStatus(data.status),
    artworkLinks: asStringArray(data.artworkLinks),
    socialLinks: asStringArray(data.socialLinks),
    socialMediaLinks: asStringArray(data.socialMediaLinks),
    websiteLinks: asStringArray(data.websiteLinks),
    shopLinks: asStringArray(data.shopLinks),
    artworkFiles: [],
    communicationMethod: asString(data.communicationMethod),
    comment: asString(data.comment),
    createdAt: asString(data.createdAt),
  };
};

const resolveArtworkFiles = async (value: unknown): Promise<ArtworkAsset[]> => {
  if (!Array.isArray(value) || value.length === 0) return [];

  const assets = await Promise.all(value.map(async (entry, index) => {
    const entryKey = `art-${index}`;

    if (typeof entry === 'string') {
      const raw = entry.trim();
      if (!raw) return null;
      if (raw.startsWith('http://') || raw.startsWith('https://')) {
        return { key: entryKey, label: raw, href: raw, thumbnail: raw };
      }
      try {
        const signed = await getSignedUrlForFile(raw);
        return {
          key: entryKey,
          label: raw,
          href: signed.signedUrl,
          thumbnail: signed.thumbnailSignedUrl ?? signed.signedUrl,
        };
      } catch {
        return { key: entryKey, label: raw, href: null, thumbnail: null };
      }
    }

    if (!isRecord(entry)) return null;

    const url = asString(entry.url) || asString(entry.signedUrl) || asString(entry.downloadUrl);
    const thumbnail = asString(entry.thumbnailSignedUrl) || url;
    const fileId = asString(entry.id) || asString(entry.fileId) || asString(entry.uploadedFileId);
    const label = asString(entry.filename) || asString(entry.objectName) || fileId || url || entryKey;

    if (url) {
      return {
        key: entryKey,
        label,
        href: url,
        thumbnail,
      };
    }

    if (fileId) {
      try {
        const signed = await getSignedUrlForFile(fileId);
        return {
          key: entryKey,
          label,
          href: signed.signedUrl,
          thumbnail: signed.thumbnailSignedUrl ?? signed.signedUrl,
        };
      } catch {
        return { key: entryKey, label, href: null, thumbnail: null };
      }
    }

    return { key: entryKey, label, href: null, thumbnail: null };
  }));

  const resolved: ArtworkAsset[] = [];
  for (const asset of assets) {
    if (asset) {
      resolved.push(asset);
    }
  }

  return resolved;
};

export const CreatorApplicationsAdmin = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [applications, setApplications] = useState<CreatorApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState<CreatorApplication | null>(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [filter, setFilter] = useState<string>('PENDING');
  const [decisionNotes, setDecisionNotes] = useState('');
  const [isDecisionLoading, setIsDecisionLoading] = useState(false);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api.get(`/admin/discounts/creator-applications${params}`);
      const apps = response.data.items || response.data.applications || response.data || [];
      setApplications(Array.isArray(apps) ? apps.map(normalizeListApplication) : []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast({
        title: t('common.error', 'Error'),
        description: t('admin.creatorApplications.fetchError', 'Failed to load applications'),
        variant: 'destructive',
      });
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [filter, t, toast]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const openApplication = useCallback(async (app: CreatorApplication) => {
    setSelectedApp(app);
    setDecisionNotes('');
    setIsDetailsLoading(true);

    try {
      const response = await api.get(`/admin/discounts/creator-applications/${app.id}`);
      const details = normalizeListApplication(response.data);
      const artworkFiles = await resolveArtworkFiles(isRecord(response.data) ? response.data.artworkFiles : undefined);

      setSelectedApp({
        ...app,
        ...details,
        artworkFiles,
      });
    } catch (error) {
      console.error('Failed to fetch application details:', error);
      toast({
        title: t('common.error', 'Error'),
        description: t('admin.creatorApplications.fetchDetailsError', 'Failed to load full application details'),
        variant: 'destructive',
      });
    } finally {
      setIsDetailsLoading(false);
    }
  }, [t, toast]);

  const handleDecision = async (
    applicationId: string,
    decision: CreatorApplicationStatus
  ) => {
    try {
      setIsDecisionLoading(true);
      await api.post(`/admin/discounts/creator-applications/${applicationId}/decision`, {
        decision,
        notes: decisionNotes,
      });
      
      toast({
        title: t('common.success', 'Success'),
        description: t(`admin.creatorApplications.decisionSuccess.${decision}`, `Application ${decision.toLowerCase()} successfully`),
      });
      
      setDecisionNotes('');
      setSelectedApp(null);
      fetchApplications();
    } catch (error) {
      console.error('Failed to submit decision:', error);
      const apiMessage = isAxiosError(error)
        ? (typeof error.response?.data?.message === 'string' ? error.response.data.message : '')
        : '';
      toast({
        title: t('common.error', 'Error'),
        description: apiMessage || t('admin.creatorApplications.decisionError', 'Failed to process decision'),
        variant: 'destructive',
      });
    } finally {
      setIsDecisionLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="outline">PENDING</Badge>;
      case 'APPROVED':
        return <Badge variant="secondary">APPROVED</Badge>;
      case 'REJECTED':
        return <Badge variant="destructive">REJECTED</Badge>;
      case 'REQUEST_INFO':
        return <Badge variant="outline">INFO REQUESTED</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchApplications} 
            disabled={loading}
            className="h-9"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
          </Button>
          <div className="flex bg-muted p-1 rounded-lg">
            {(['PENDING', 'APPROVED', 'REJECTED', 'REQUEST_INFO', 'all'] as const).map((s) => (
              <Button
                key={s}
                variant="ghost"
                size="sm"
                onClick={() => setFilter(s)}
                aria-pressed={filter === s}
                className={`h-7 px-3 text-xs font-semibold transition-all ${
                  filter === s
                    ? 'bg-background text-foreground border border-border shadow-sm hover:bg-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {s === 'all' ? t('admin.filters.all', 'All') : t(`admin.filters.${s.toLowerCase()}`, s)}
              </Button>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground font-medium">
          {applications.length} {t('admin.creatorApplications.count', 'applications found')}
        </p>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="w-[250px] font-bold py-4">{t('admin.creatorApplications.applicant', 'Applicant')}</TableHead>
              <TableHead className="font-bold py-4">{t('admin.creatorApplications.level', 'Program')}</TableHead>
              <TableHead className="font-bold py-4">{t('admin.creatorApplications.status', 'Status')}</TableHead>
              <TableHead className="font-bold py-4">{t('admin.creatorApplications.date', 'Submitted')}</TableHead>
              <TableHead className="text-right font-bold py-4">{t('admin.creatorApplications.actions', 'Actions')}</TableHead>
            </TableRow>
          </TableHeader>
                  <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-32 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground font-medium">{t('common.loading', 'Loading...')}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : applications.length > 0 ? (
              applications.map((app) => (
                <TableRow key={app.id} className="group hover:bg-muted/30 transition-colors">
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                        {app.userName?.[0] || 'U'}
                      </div>
                      <div>
                        <p className="font-bold text-sm leading-none mb-1">{app.userName}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 italic">
                          <Mail className="h-3 w-3" /> {app.userEmail}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center gap-2">
                      <Trophy className={`h-4 w-4 ${app.requestedLevel.includes('30') ? 'text-amber-500' : 'text-slate-400'}`} />
                      <span className="text-xs font-bold tracking-tight">
                        {app.requestedLevel.replace('_', ' ')}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">{getStatusBadge(app.status)}</TableCell>
                  <TableCell className="py-4 text-xs font-medium text-muted-foreground">
                    <div className="flex items-center gap-1.5 italic">
                      <Calendar className="h-3 w-3" />
                      {new Date(app.createdAt).toLocaleDateString(undefined, { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="py-4 text-right">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => void openApplication(app)}
                      className="h-8 px-3 text-xs font-bold hover:bg-primary hover:text-primary-foreground transition-all"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      {t('admin.creatorApplications.review', 'Review')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-32 text-center">
                  <div className="flex flex-col items-center justify-center gap-1 py-8">
                    <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-2">
                      <X className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-bold text-foreground">
                      {t('admin.creatorApplications.noApplications', 'No applications found')}
                    </p>
                    <p className="text-xs text-muted-foreground italic">
                      {t('admin.creatorApplications.tryAnotherFilter', 'Try changing the status filter')}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog 
        open={!!selectedApp} 
        onOpenChange={(open) => {
          if (!open) {
            setSelectedApp(null);
            setDecisionNotes('');
            setIsDetailsLoading(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedApp && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xl font-bold">
                    {selectedApp.userName?.[0] || 'U'}
                  </div>
                  <div className="text-left">
                    <DialogTitle className="text-xl font-bold leading-none mb-1">
                      {selectedApp.userName}
                    </DialogTitle>
                    <DialogDescription className="text-xs italic">
                      {t('admin.creatorApplications.reviewDesc', 'Reviewing application for')} {selectedApp.requestedLevel.replace('_', ' ')}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4 border-y border-muted/50 my-2">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                      <User className="h-3 w-3" /> {t('admin.creatorApplications.applicantDetails', 'Applicant Details')}
                    </h4>
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <p className="text-sm font-medium flex justify-between">
                        <span className="text-muted-foreground italic">{t('common.email', 'Email')}:</span>
                        <span className="font-bold">{selectedApp.userEmail}</span>
                      </p>
                      <p className="text-sm font-medium flex justify-between">
                        <span className="text-muted-foreground italic">{t('admin.creatorApplications.commMethod', 'Contact via')}:</span>
                        <Badge variant="secondary" className="text-[10px] font-bold">{selectedApp.communicationMethod || 'N/A'}</Badge>
                      </p>
                    </div>
                  </div>

                  {selectedApp.comment && (
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                        <MessageSquare className="h-3 w-3" /> {t('admin.creatorApplications.comment', 'Applicant Comment')}
                      </h4>
                      <div className="bg-muted/30 rounded-lg p-3 border-l-2 border-primary/30">
                        <p className="text-xs leading-relaxed italic text-foreground/80">"{selectedApp.comment}"</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                    <LinkIcon className="h-3 w-3" /> {t('admin.creatorApplications.links', 'Portfolio & Socials')}
                  </h4>
                  {isDetailsLoading ? (
                    <div className="flex min-h-[120px] items-center justify-center rounded-lg border bg-muted/20">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedApp.artworkFiles.length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-[10px] font-bold text-muted-foreground italic px-1">
                            {t('admin.creatorApplications.uploadedArtwork', 'Uploaded pictures')}
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedApp.artworkFiles.map((file) => (
                              <div key={file.key} className="rounded-md border bg-muted/20 overflow-hidden">
                                {file.href ? (
                                  <a href={file.href} target="_blank" rel="noopener noreferrer" className="block">
                                    {file.thumbnail ? (
                                      <img
                                        src={file.thumbnail}
                                        alt={file.label}
                                        className="h-24 w-full object-cover"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="h-24 w-full flex items-center justify-center text-muted-foreground">
                                        <ImageIcon className="h-5 w-5" />
                                      </div>
                                    )}
                                  </a>
                                ) : (
                                  <div className="h-24 w-full flex items-center justify-center text-muted-foreground">
                                    <ImageIcon className="h-5 w-5" />
                                  </div>
                                )}
                                <p className="truncate px-2 py-1 text-[10px] text-muted-foreground" title={file.label}>
                                  {file.label}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {[
                        { label: t('admin.creatorApplications.artwork', 'Artworks'), links: selectedApp.artworkLinks },
                        { label: t('admin.creatorApplications.social', 'Socials'), links: mergeUniqueStrings(selectedApp.socialLinks, selectedApp.socialMediaLinks) },
                        { label: t('admin.creatorApplications.web', 'Websites / Shops'), links: mergeUniqueStrings(selectedApp.websiteLinks, selectedApp.shopLinks) },
                      ].map((section, idx) => section.links.length > 0 && (
                        <div key={idx} className="space-y-1.5">
                          <p className="text-[10px] font-bold text-muted-foreground italic px-1">{section.label}</p>
                          <div className="flex flex-col gap-1.5">
                            {section.links.map((link, i) => (
                              <a
                                key={i}
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-[11px] font-medium text-foreground bg-muted/20 hover:bg-muted/35 border rounded-md px-3 py-2 transition-colors truncate"
                              >
                                <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                                <span className="truncate">{link}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}

                      {selectedApp.artworkFiles.length === 0 &&
                        selectedApp.artworkLinks.length === 0 &&
                        selectedApp.socialLinks.length === 0 &&
                        selectedApp.socialMediaLinks.length === 0 &&
                        selectedApp.websiteLinks.length === 0 &&
                        selectedApp.shopLinks.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">
                          {t('admin.creatorApplications.noLinksProvided', 'No portfolio links or uploaded files provided')}
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4 pt-2">
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <MessageCircle className="h-3 w-3" /> {t('admin.creatorApplications.decisionNotes', 'Decision Notes & Feedback')}
                </h4>
                <Textarea
                  placeholder={t('admin.creatorApplications.notesPlaceholder', 'Enter notes for the user or internal use...')}
                  className="min-h-[100px] text-sm resize-none focus-visible:ring-primary shadow-inner bg-muted/20"
                  value={decisionNotes}
                  onChange={(e) => setDecisionNotes(e.target.value)}
                />
              </div>

              {(selectedApp.status === 'APPROVED' || selectedApp.status === 'REJECTED') ? (
                <p className="mt-3 text-xs text-muted-foreground italic">
                  {t(
                    'admin.creatorApplications.finalizedHint',
                    'This application is already finalized. Decision buttons are disabled.',
                  )}
                </p>
              ) : null}

              <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0 mt-6 pt-6 border-t border-muted/50">
                <div className="flex flex-1 gap-2">
                  <Button
                    variant="destructive"
                    className="flex-1 font-bold h-10 shadow-sm"
                    onClick={() => handleDecision(selectedApp.id, 'REJECTED')}
                    disabled={isDecisionLoading || selectedApp.status === 'APPROVED' || selectedApp.status === 'REJECTED'}
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t('admin.creatorApplications.reject', 'Reject')}
                  </Button>
                  <Button
                    variant="secondary"
                    className="flex-1 font-bold h-10 shadow-sm"
                    onClick={() => handleDecision(selectedApp.id, 'REQUEST_INFO')}
                    disabled={isDecisionLoading || selectedApp.status === 'APPROVED' || selectedApp.status === 'REJECTED'}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    {t('admin.creatorApplications.requestInfo', 'Info')}
                  </Button>
                </div>
                <Button
                  className="flex-1 sm:flex-initial sm:min-w-[140px] font-bold h-10 shadow-sm"
                  onClick={() => handleDecision(selectedApp.id, 'APPROVED')}
                  disabled={isDecisionLoading || selectedApp.status === 'APPROVED' || selectedApp.status === 'REJECTED'}
                >
                  {isDecisionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                  {t('admin.creatorApplications.approve', 'Approve')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
